import type {
    AlignChildren,
    Asset,
    AssetGroup,
    SceneGroup,
    TextAlignment,
    TextAsset,
} from '@lumen5/luminary';
import { GraphicClassTypes, VideoData } from '@lumen5/luminary';

import { getTemplateTextMaxSize, getTemplateTextMinSize } from 'js/src/data/templates';
import {
    setTextAlignmentUserOverride,
    setTextPositionUserOverride,
    setTextSizeUserOverride,
} from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import type { TextPosition } from 'js/src/editor/qa/qa-types';
import { setObjectPropertyValue } from 'js/src/editor/util';
import uuid from 'js/src/libs/uuid';
import { isVoiceDrivenSceneGroup, saveRawLuminary } from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getCurrentGlobalCaptionSceneDesignInUse,
    setAssetPositionTransformationOperation,
} from 'js/src/luminary/luminary-operations';
import { getCaptionType, getRootAssetGroup } from 'js/src/luminary/luminary-selectors';

/**
 * Sets the text position for a slide, and sets the user override flag so the text
 * position doesn't automatically get reset again
 */
export const setSlideTextPositionOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneId: string,
        textPosition: TextPosition,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error(`Scene with id ${sceneId} not found`);
        }
        const isEditingText = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .some((textAsset) => textAsset.hasTextEditingEnabled);
        const applyTextPosition = () => {
            luminary.batchRun(() => {
                scene.applyTextPreset(textPosition);
                scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((textAsset) => {
                    textAsset.setFontSizeToFitAllText();
                });
                scene.getAssetsByType(VideoData.FANCY_CAPTION_ASSET_TYPE).forEach((asset) => {
                    const { fontSize: maxSize } = asset.getFontSizeToFitTallestText({
                        min: getTemplateTextMinSize(getState(), luminary.template.id)!,
                        max: getTemplateTextMaxSize(getState(), luminary.template.id)!,
                    });
                    if (asset.textSize > maxSize) {
                        asset.textSize = maxSize;
                    }
                });
            });
        };
        if (isEditingText) {
            luminary.restoreCursorAfterEffect(() => {
                applyTextPosition();
            });
        } else {
            applyTextPosition();
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        // set user override for slide's text position to overridden (not automatic)
        dispatch(
            setTextPositionUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextAlignmentUserOverride({
                sceneId,
                isAutomatic: true,
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Media Step: Text Position Updated', {
            sceneId,
        });
    },
);

/**
 * Update a color property of a text asset
 */
export const setTextColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        colorPropertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setTextColorOperation: scene not found');
        }

        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('setTextColorOperation: asset not found in scene');
        }

        const isTextAssetOrGroup = VideoData.isTextAsset(asset) || VideoData.isAssetGroup(asset);

        if (isTextAssetOrGroup) {
            const setTextColor = () => {
                setObjectPropertyValue(asset, colorPropertyName, newColor);
            };
            // There is a input in the text color picker and when it is used to change the text color there is no cursor position to restore so skip it
            const anchorNode = window.getSelection()?.anchorNode;
            const restoreCursor = !(
                anchorNode &&
                anchorNode instanceof HTMLDivElement &&
                anchorNode.classList.contains('prevent-restore-cursor')
            );
            if (restoreCursor) {
                luminary.restoreCursorAfterEffect(setTextColor);
            } else {
                setTextColor();
            }
        }

        if (VideoData.isFancyCaptionAsset(asset)) {
            setObjectPropertyValue(asset, colorPropertyName, newColor);
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        if (typeof newColor !== 'string') {
            return;
        }

        // this was previously part of setTextColorByIndexOperation -- so we only keep track of analytics if the number was a palette index (i.e. a string)
        // analytics
        if (colorPropertyName === 'normalStyle.fill') {
            analytics.track('Text element color changed', {
                colorType: 'palette',
            });
        } else if (colorPropertyName === 'highlightStyle.fill') {
            analytics.track('Text element highlight color changed', {
                colorType: 'palette',
            });
        } else {
            analytics.track('Text element accent color changed', {
                colorType: 'palette',
            });
        }
    },
);

/**
 * Update a "background" color property
 */
export const setBackgroundAssetColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        propertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error(`Scene with id ${sceneId} not found`);
        }
        const asset = scene.getAssetById(assetId);
        if (asset) {
            setObjectPropertyValue(asset, propertyName, newColor);
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/** Update displayCaptions of a scene */
export const setSceneDisplayCaptionsOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        displayCaptions: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setSceneDisplayCaptionsOperation: scene not found');
        }
        scene.displayCaptions = displayCaptions;
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Update the text size of a text asset
 */
export const setTextAssetSizeOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneId: string,
        textAssetId: string,
        textSize: number,
        maintainCursor = true,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setTextAssetSizeOperation: scene not found');
        }
        const asset = scene.getAssetById(textAssetId);
        if (!asset) {
            throw new Error('setTextAssetSizeOperation: asset not found');
        }
        if (VideoData.isTextAsset(asset)) {
            if (maintainCursor) {
                luminary.restoreCursorAfterEffect(() => {
                    asset.tryToUpdateTextSize(textSize);
                });
            } else {
                asset.tryToUpdateTextSize(textSize);
            }
        } else if (VideoData.isFancyCaptionAsset(asset)) {
            const { fontSize: maxSize } = asset.getFontSizeToFitTallestText({
                min: getTemplateTextMinSize(getState(), luminary.template.id)!,
                max: getTemplateTextMaxSize(getState(), luminary.template.id)!,
            });
            asset.textSize = Math.min(textSize, maxSize);
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextSizeUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Converts the current selection made in the luminary text input to a highlight tuple that
 * will be merged with the text asset's current highlight indices
 */
export const addTextAssetHighlightOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        textAssetId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (window.getSelection()?.isCollapsed) {
            return;
        }

        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('addTextAssetHighlightOperation: scene not found');
        }
        const textAsset = scene.getAssetById(textAssetId) as TextAsset | undefined;
        if (!textAsset) {
            throw new Error('addTextAssetHighlightOperation: text asset not found');
        }
        const selectionAsRange = textAsset.getTextInputSelection();
        if (selectionAsRange === null) {
            return;
        }

        // ensure that the range is ordered ascending
        const newHighlightRange = selectionAsRange.sort((a, b) => a - b);

        // merge new highlights with existing ones, and assign
        const newHighlights = [...textAsset.textHighlights, newHighlightRange];
        luminary.restoreCursorAfterEffect(() => {
            textAsset.textHighlights = newHighlights;
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Converts the current selection made in the luminary text input to a highlight tuple that
 * will be deleted from the text asset's current highlight indices
 */
export const clearTextAssetHighlightOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        textAssetId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('clearTextAssetHighlightOperation: scene not found');
        }
        const textAsset = scene.getAssetById(textAssetId) as TextAsset | undefined;
        if (!textAsset) {
            throw new Error('clearTextAssetHighlightOperation: text asset not found');
        }
        const selectionAsRange = textAsset.getTextInputSelection();
        if (selectionAsRange === null) {
            return;
        }

        // ensure that the range is ordered ascending
        const newHighlightRange = selectionAsRange.sort((a, b) => a - b);
        const newHighlights: [number, number][] = [];
        const [startIdx, endIdx] = newHighlightRange;

        // If the cursor is collapsed, then if the cursor is at a position that "touches" an existing
        // highlighted range, remove it
        if (window.getSelection()?.isCollapsed) {
            textAsset.textHighlights.forEach((arr) => {
                const [highlightStartIdx, highlightEndIdx] = arr;
                if (startIdx < highlightStartIdx || endIdx > highlightEndIdx) {
                    newHighlights.push(arr);
                }
            });
        }
        // If the cursor isn't collapsed, only remove the highlighted subset of characters included
        // in that selection
        else {
            textAsset.textHighlights.forEach((arr) => {
                const [highlightStartIdx, highlightEndIdx] = arr;
                if (startIdx >= highlightStartIdx && endIdx <= highlightEndIdx) {
                    if (highlightStartIdx !== startIdx) {
                        newHighlights.push([highlightStartIdx, startIdx]);
                    }
                    if (endIdx !== highlightEndIdx) {
                        newHighlights.push([endIdx, highlightEndIdx]);
                    }
                } else {
                    newHighlights.push(arr);
                }
            });
        }

        luminary.restoreCursorAfterEffect(() => {
            textAsset.textHighlights = newHighlights;
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Changes the template mapping for a specific text asset to use either the primary
 * or secondary template font
 */
export const setAssetFontUrlOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneId: string,
        assetId: string,
        fontIndex: number, // index from the template's font array
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('setAssetFontUrlOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('setAssetFontUrlOperation: asset not found');
        }
        const fontUrl = VideoData.templateFontsByIndex(fontIndex);
        if (VideoData.isTextAsset(asset)) {
            luminary.restoreCursorAfterEffect(() => {
                const currentFont = asset.normalStyle.fontUrl;
                const currentHighlightFont = asset.highlightStyle.fontUrl;

                // Keep normal and highlight font in sync if they are the same
                if (currentHighlightFont === currentFont) {
                    setObjectPropertyValue(asset, 'highlightStyle.fontUrl', fontUrl);
                }
                setObjectPropertyValue(asset, 'normalStyle.fontUrl', fontUrl);

                asset.setFontSizeToFitAllText();
            });
        } else if (VideoData.isFancyCaptionAsset(asset)) {
            setObjectPropertyValue(asset, 'normalStyle.fontUrl', fontUrl);

            // if the text is too tall to fit in the bounds with the new font, reduce the font size
            const { fontSize: maxSize } = asset.getFontSizeToFitTallestText({
                min: getTemplateTextMinSize(getState(), luminary.template.id)!,
                max: getTemplateTextMaxSize(getState(), luminary.template.id)!,
            });
            if (asset.textSize > maxSize) {
                asset.textSize = maxSize;
            }
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const setCaptionsSceneDesignOperation = createLuminaryOperation(
    ({ luminary, call }, newSceneDesignId: string) => {
        const positionsToRestore: Array<{
            sceneGroupId: string;
            x: number;
            y: number;
        }> = [];

        luminary.batchRun(() => {
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (!isVoiceDrivenSceneGroup(sceneGroup)) {
                    return;
                }

                const captionAsset = sceneGroup.captions.asset;

                if (captionAsset && captionAsset.hasUserChangedPosition) {
                    positionsToRestore.push({
                        sceneGroupId: sceneGroup.id,
                        x: captionAsset.transformation.position.x,
                        y: captionAsset.transformation.position.y,
                    });
                }

                sceneGroup.captions.sceneDesignId = newSceneDesignId;
            });
        });

        // We need to re-apply any user-changed positions AFTER changing the scene design,
        // as changing the scene design resets the caption asset and its position
        positionsToRestore.forEach(({ sceneGroupId, x, y }) => {
            const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
            if (!sceneGroup) {
                return;
            }
            const newCaptions = sceneGroup.captions;
            if (!newCaptions) {
                return;
            }
            const newCaptionAsset = sceneGroup.captions.asset;

            call(setAssetPositionTransformationOperation, {
                sceneGroupId,
                assetId: newCaptionAsset.id,
                x,
                y,
            });
        });

        const newCaptionType = getCaptionType(luminary);
        analytics.track('Captions scene design changed', {
            sceneDesignId: newSceneDesignId,
            captionType: newCaptionType,
        });
    },
);

/**
 * Update a color property of all the captions in scene group. Note that this could be a
 * text fill color or a colorGraphics color!
 */
export const setCaptionsColorOperation = createLuminaryOperation(
    ({ luminary }, colorPropertyName: string, newColor: number | string) => {
        const currentCaptionSceneDesign = getCurrentGlobalCaptionSceneDesignInUse(luminary);
        luminary.batchRun(() => {
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (currentCaptionSceneDesign !== sceneGroup.captions.sceneDesignId) {
                    return;
                }
                if (!isVoiceDrivenSceneGroup(sceneGroup)) {
                    return;
                }

                setObjectPropertyValue(sceneGroup.captions.asset, colorPropertyName, newColor);
            });
        });
    },
);

/**
 * Update the text size of the captions for all voicedriven scenegroups
 */
export const setCaptionsTextSizeOperation = createLuminaryOperation(
    ({ luminary }, textSize: number) => {
        const currentCaptionSceneDesign = getCurrentGlobalCaptionSceneDesignInUse(luminary);
        luminary.batchRun(() => {
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (currentCaptionSceneDesign !== sceneGroup.captions.sceneDesignId) {
                    return;
                }
                if (!isVoiceDrivenSceneGroup(sceneGroup)) {
                    return;
                }

                sceneGroup.captions.asset.textSize = textSize;
            });
        });
    },
);

/**
 * Changes the template mapping to all captions in sceneGroup
 * to use either the primary or secondary template font
 */
export const setCaptionsFontUrlOperation = createLuminaryOperation(
    ({ luminary }, fontIndex: number) => {
        if (fontIndex < 0) {
            throw new Error('Font index must be greater than or equal to 0');
        }
        const currentCaptionSceneDesign = getCurrentGlobalCaptionSceneDesignInUse(luminary);

        luminary.batchRun(() => {
            luminary.sceneGroups.forEach((sceneGroup) => {
                if (!isVoiceDrivenSceneGroup(sceneGroup)) {
                    return;
                }
                if (currentCaptionSceneDesign !== sceneGroup.captions.sceneDesignId) {
                    return;
                }
                setObjectPropertyValue(
                    sceneGroup.captions.asset,
                    'normalStyle.fontUrl',
                    VideoData.templateFontsByIndex(fontIndex),
                );
            });
        });
    },
);

export const setAllSceneGroupsCaptionVisibilityOperation = createLuminaryOperation(
    ({ luminary, call }, visible: boolean) => {
        luminary.batchRun(() => {
            luminary.sceneGroups.forEach((sceneGroup) => {
                call(setSceneGroupCaptionsVisibilityOperation, sceneGroup.id, visible);
            });
        });
    },
);

/**
 * Changes the caption visibility to all captions in sceneGroup
 */
export const setSceneGroupCaptionsVisibilityOperation = createLuminaryOperation(
    ({ luminary, dispatch }, sceneGroupId: string, isVisible: boolean) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        sceneGroup.captions.visibleWithUpdate = isVisible;
    },
);

/**
 * Copies caption styles (scene design ID and granular overrides) from existing voice-driven
 * scene groups to a specific target scene group. This is used when adding new voice-driven
 * scenes to ensure caption style consistency across the video.
 */
export const applyGlobalCaptionStylesToSceneGroupOperation = createLuminaryOperation(
    ({ luminary }, sceneGroup: SceneGroup) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        const sceneGroupWithCaptions = luminary.sceneGroups.find(
            (sg) =>
                isVoiceDrivenSceneGroup(sg) &&
                sg.id !== sceneGroup.id &&
                sg.captions.sceneDesignId !== null,
        );
        if (!sceneGroupWithCaptions) {
            return;
        }

        const captionSceneDesignId = sceneGroupWithCaptions.captions.sceneDesignId;
        sceneGroup.captions.sceneDesignId = captionSceneDesignId;

        const captionAsset = sceneGroupWithCaptions.captions.asset;
        if (!captionAsset) {
            return;
        }

        const fontPropertyPath = 'normalStyle.fontUrl';
        const fontIndex = VideoData.getTemplateIndexFromAssetProperty(
            captionAsset,
            fontPropertyPath,
        );
        if (fontIndex >= 0) {
            setObjectPropertyValue(
                sceneGroup.captions.asset,
                fontPropertyPath,
                VideoData.templateFontsByIndex(fontIndex),
            );
        }

        sceneGroup.captions.asset.textSize = captionAsset.textSize;

        const fillPropertyPath = 'normalStyle.fill';
        const colorIndex = VideoData.getTemplateIndexFromAssetProperty(
            captionAsset,
            fillPropertyPath,
        );
        if (colorIndex >= 0) {
            setObjectPropertyValue(
                sceneGroup.captions.asset,
                fillPropertyPath,
                VideoData.templateColorByIndex(colorIndex),
            );
        }

        captionAsset.normalStyle.graphics.forEach((graphic, graphicIndex) => {
            const graphicFillPropertyPath = `normalStyle.graphics.${graphicIndex}.fill`;
            const graphicColorIndex = VideoData.getTemplateIndexFromAssetProperty(
                captionAsset,
                graphicFillPropertyPath,
            );
            if (graphicColorIndex >= 0) {
                setObjectPropertyValue(
                    sceneGroup.captions.asset,
                    graphicFillPropertyPath,
                    VideoData.templateColorByIndex(graphicColorIndex),
                );
            }
        });
    },
);

/**
 * Sets the alignment (left | center | right) for either an individual text asset, or recursively for an asset group of text
 */

export function updatePositionAndAnchor(
    asset: Asset,
    currentWidth: number,
    newAlignment: TextAlignment | AlignChildren,
): void {
    const alignmentToAnchor: Record<TextAlignment, number> = {
        left: 0,
        center: 0.5,
        right: 1,
    } as const;

    const currentAnchorX = asset.transformation.anchor.x;
    const currentPositionX = asset.transformation.position.x;

    const newAnchorX = alignmentToAnchor[newAlignment];
    const newPositionX = currentPositionX + (newAnchorX - currentAnchorX) * currentWidth;

    asset.transformation.anchor.x = newAnchorX;
    asset.transformation.position.x = newPositionX;
}

function groupHasCircleGraphic(asset: AssetGroup): boolean {
    return asset.colorGraphics.some((graphic) =>
        graphic.getCurrentGraphicsClassTypes().includes(GraphicClassTypes.CircleGraphic),
    );
}

export const setTextAssetAlignmentOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        alignment: TextAlignment,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) throw new Error('setTextAssetAlignmentOperation: scene not found');

        const asset = scene.getAssetById(assetId);
        if (!asset) throw new Error('setTextAssetAlignmentOperation: asset not found');

        const rootAssetGroup = VideoData.isAssetGroup(asset) ? asset : getRootAssetGroup(asset);

        // Currently, when we draw the blue outline to highlight the asset, in the CSS we add an offset
        // of 5px to each side of the bounds. We want to make sure to also consider this when shrinking
        // the maxWidth, so that those visual bounds don't appear to go off canvas.
        const offset = 10;

        if (
            rootAssetGroup &&
            VideoData.isAssetGroup(rootAssetGroup) &&
            rootAssetGroup.alignChildren !== alignment
        ) {
            const largestTextAsset = rootAssetGroup.getLargestTextAsset();
            if (!largestTextAsset) {
                return;
            }

            rootAssetGroup.applyAlignmentRecursively(alignment);

            // Circle graphics are a bit of an edge case, as they don't "grow"
            // with the text, in the same way that line and box graphics do.
            // The anchor should always stay the same in this case, so we only need
            // to update the alignment, and can skip the rest.
            if (groupHasCircleGraphic(rootAssetGroup)) {
                return;
            }

            updatePositionAndAnchor(rootAssetGroup, largestTextAsset.getBounds().width, alignment);
            rootAssetGroup.adjustAllAssetMaxWidthsToFitResolution(offset);
        } else if (
            (VideoData.isFancyCaptionAsset(asset) || VideoData.isTextAsset(asset)) &&
            asset.textAlign !== alignment
        ) {
            asset.textAlign = alignment;

            updatePositionAndAnchor(asset, asset.getBounds().width, alignment);
            asset.adjustMaxWidthToFitResolution(offset);
        } else {
            return;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTextAlignmentUserOverride({
                sceneId,
                isAutomatic: false,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
